import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import { useTMInvoiceData } from "@/hooks/use-tm-invoice-data";
import { TMInvoicePreviewDialog, type TMInvoicePrefill } from "./TMInvoicePreviewDialog";

/**
 * VIB-800: Faktura-seksjon for T&M-prosjekter (project_type='hourly').
 * Viser ufakturerte timer/bilag-sammendrag + «Opprett T&M-faktura»-knapp.
 *
 * VIB-994: gjenbrukes også på fastpris-prosjekter, scopet til ETT godkjent
 * T&M-mersalg (`upsellId` + `upsellDescription` satt). FakturaTab renderer én
 * seksjon per godkjent T&M-mersalg — draft T&M-mersalg vises IKKE her
 * (blokkerer T&M-fakturering av ikke-godkjent mersalg, jf. VIB-665-gaten).
 */
export function TMInvoiceSection({
  projectId,
  projectName,
  canCreateInvoice,
  onProceed,
  upsellId,
  upsellDescription,
}: {
  projectId: string;
  projectName: string;
  canCreateInvoice: boolean;
  onProceed: (prefill: TMInvoicePrefill) => void;
  /** VIB-994: satt = scopet til ett T&M-mersalg i stedet for hele prosjektet. */
  upsellId?: string;
  upsellDescription?: string;
}) {
  const { data, isLoading } = useTMInvoiceData(projectId, upsellId ? { upsellId } : undefined);
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-display text-lg text-foreground tracking-wide flex items-center gap-2">
              <SrIcon name="receipt" className="w-5 h-5 text-primary" />
              {upsellDescription ? `T&M-fakturering — ${upsellDescription}` : "T&M-fakturering"}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {isLoading
                ? "Laster …"
                : data?.hasUninvoiced
                  ? `${data.timeLines.length} ansatte med ufakturerte timer (${formatNOK(data.timeTotal)}) · ${data.vouchers.length} ufakturerte bilag`
                  : "Ingen ufakturerte timer eller bilag"}
            </p>
          </div>
          {canCreateInvoice && (
            <Button onClick={() => setOpen(true)} disabled={isLoading || !data?.hasUninvoiced}>
              Opprett T&M-faktura
            </Button>
          )}
        </div>
      </CardContent>
      <TMInvoicePreviewDialog
        projectId={projectId}
        projectName={projectName}
        open={open}
        onOpenChange={setOpen}
        onProceed={onProceed}
        upsellId={upsellId}
      />
    </Card>
  );
}
